import React, { MouseEventHandler } from "react";

import ParticipantsIcon from "@uui/Users03";

import Typography from "@mui/material/Typography";

import { isFreePlan } from "@app/analytics/account/Billing/utils";
import CountChip from "@app/analytics/components/CountChip";
import { TableCell } from "@app/analytics/components/Table";
import useBillingContext from "@app/analytics/hooks/billing/useBillingContext";
import useUpdateMeetingName from "@app/analytics/hooks/meetings/useUpdateMeetingName";
import EditableReportTitle from "@app/analytics/MeetingReports/components/EditableReportTitle";
import useReportsTable from "@app/analytics/MeetingReports/stores/reportsTable";
import useMeasure from "@app/common/hooks/useMeasure";
import type { UserMeeting } from "@app/common/types";
import type { Tag as ITag } from "@app/common/types";
import { pluralizePhrase } from "@app/common/utils";
import * as SessionUtils from "@app/common/utils/sessionUtils";

import { Link } from "@rui/Link";
import {
  Column,
  DesktopOnlyRow,
  MobileOnlyColumn,
  MobileOnlyRow,
  Row,
} from "@rui/Stack";

import { ReportAlert } from "./styles";
import DenseReportCellContent from "../cells/DenseReportCellContent";

const CELL_PADDING_PX = 16;

const EditableMeetingTitle = ({
  report,
  maxWidth,
}: {
  report: UserMeeting;
  maxWidth: number;
}) => {
  const { updateMeetingName } = useUpdateMeetingName(report);

  const handleEditTitle = (title: string) => {
    updateMeetingName(title);
  };

  return (
    <EditableReportTitle
      report={report}
      maxWidth={maxWidth}
      onEditChange={handleEditTitle}
    />
  );
};

interface MeetingTitleCellDenseContent {
  isSummary?: boolean;
  tags?: ITag[];
  reason?: React.ReactNode;
  details?: React.ReactNode;
  onTagClick?: MouseEventHandler<HTMLDivElement>;
}

const MeetingTitleCell = ({
  isEditable = false,
  onClickUpgradeDetails,
  report,
  suppressAlerts = false,
  meetingTitleCellDenseContent,
}: {
  isEditable?: boolean;
  onClickUpgradeDetails?: () => void;
  report: UserMeeting;
  suppressAlerts?: boolean;
  meetingTitleCellDenseContent?: MeetingTitleCellDenseContent;
}) => {
  const editReport = useReportsTable((state) =>
    isEditable ? state.editReport : null
  );
  const [cellRef, , { innerWidth: cellWidth }] = useMeasure();

  const { currentPlan } = useBillingContext();

  const isQuotaExceededReport =
    !suppressAlerts && SessionUtils.isRestricted(report);
  const showQuotaExceeded =
    isQuotaExceededReport && isFreePlan(currentPlan.type);
  const isDataDeleted =
    isQuotaExceededReport && SessionUtils.isDataDeleted(report);
  const isReadLite = !suppressAlerts && SessionUtils.isReadLite(report);

  const handleClickUpgradeDetails: React.MouseEventHandler = (event) => {
    event.stopPropagation();
    onClickUpgradeDetails?.();
  };

  const isEditingMeetingTitle =
    editReport != null &&
    editReport.id === report.id &&
    editReport.type === "session";

  const countChip = report.numParticipants > 0 && (
    <CountChip
      Icon={ParticipantsIcon}
      tooltip={pluralizePhrase(report.numParticipants, "participant")}
    >
      {report.numParticipants}
    </CountChip>
  );

  return (
    <TableCell ref={cellRef} sx={{ maxWidth: { xxs: "220px", sm: "300px" } }}>
      <Column spacing={0.5} overflow="hidden">
        <Row gap={0.25}>
          {isEditingMeetingTitle ? (
            <EditableMeetingTitle
              report={report}
              maxWidth={cellWidth - CELL_PADDING_PX}
            />
          ) : (
            <Typography
              variant="bodySecondaryMed"
              color="shades.neutral.800"
              noWrap
              title={report.title}
            >
              {report.title}
            </Typography>
          )}
          <MobileOnlyRow>{countChip}</MobileOnlyRow>
        </Row>
        <Row variant="left" spacing={0.5}>
          <DesktopOnlyRow>{countChip}</DesktopOnlyRow>
          {isReadLite ? (
            <ReportAlert
              severity="error"
              title="No recording"
              description="Zoom meeting access is disabled"
            />
          ) : isDataDeleted ? (
            <ReportAlert
              title="Quota exceeded"
              description="Data cannot be reprocessed"
            />
          ) : showQuotaExceeded ? (
            <ReportAlert
              title="Quota exceeded"
              description="Upgrade to generate a report"
              action={<Link onClick={handleClickUpgradeDetails}>Details</Link>}
            />
          ) : null}
        </Row>
      </Column>
      {meetingTitleCellDenseContent && (
        <MobileOnlyColumn>
          <DenseReportCellContent
            report={report}
            {...meetingTitleCellDenseContent}
          />
        </MobileOnlyColumn>
      )}
    </TableCell>
  );
};

export default MeetingTitleCell;
